/**
 * <kallo-gallery> — the belt product page's image gallery.
 *
 * The stage is a scroll-snap strip, so swiping it is the browser's own
 * behaviour and the gallery still works with this file absent. Everything here
 * is the layer on top: arrows that step through, thumbnails that jump, and the
 * highlight that keeps the two in agreement however the visitor moved.
 *
 * Position is read back off the stage's scroll rather than held in a variable,
 * which means a swipe, an arrow and a thumbnail click all end up in the same
 * place with no state to keep in sync.
 */
(function () {
  'use strict';

  class KalloGallery extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      this.stage = this.querySelector('[data-gallery-stage]');
      this.track = this.querySelector('[data-gallery-track]');
      this.prev = this.querySelector('[data-gallery-prev]');
      this.next = this.querySelector('[data-gallery-next]');
      this.collect();

      if (!this.stage || this.slides.length < 2) return;

      this.index = 0;

      // A thumbnail's place among the ones showing, not among all of them,
      // since pictures for other choices are hidden.
      this.querySelectorAll('[data-gallery-thumb]').forEach((thumb) => {
        thumb.addEventListener('click', () => this.go(this.thumbs.indexOf(thumb)));
      });

      if (this.prev) this.prev.addEventListener('click', () => this.go(this.index - 1));
      if (this.next) this.next.addEventListener('click', () => this.go(this.index + 1));

      // A swipe moves the stage without going through `go`, so the highlight
      // follows the scroll position instead of the control that was used.
      this.stage.addEventListener('scroll', () => this.queueSync(), { passive: true });

      this.sync();
    }

    /** The slides and thumbnails showing; hidden ones are skipped. */
    collect() {
      this.slides = Array.from(this.querySelectorAll('[data-gallery-slide]:not([hidden])'));
      this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]:not([hidden])'));
    }

    /**
     * Shows only the pictures made for the given colour and plate: one with
     * `data-only-color` or `data-only-plate` set must match, one without shows
     * for any. Then goes back to the first picture.
     */
    filter(color, plate) {
      const matches = (el) =>
        (!el.dataset.onlyColor || el.dataset.onlyColor === color) &&
        (!el.dataset.onlyPlate || el.dataset.onlyPlate === plate);
      this.querySelectorAll('[data-gallery-slide], [data-gallery-thumb]').forEach((el) => {
        el.hidden = !matches(el);
      });
      this.collect();
      this.go(0);
    }

    go(index) {
      if (!this.stage || this.slides.length === 0) return;
      const clamped = Math.max(0, Math.min(index, this.slides.length - 1));
      // Assigning `scrollLeft` rather than calling `scrollTo` with
      // `behavior: 'smooth'`: on a container with `scroll-snap-type: mandatory`
      // the scripted smooth scroll is cancelled by the snap and the strip never
      // moves. The animation comes from `scroll-behavior` in CSS instead, which
      // the snap leaves alone.
      this.stage.scrollLeft = this.slides[clamped].offsetLeft - this.slides[0].offsetLeft;
      // Move the highlight now rather than waiting for the smooth scroll to
      // settle, so the control feels answered immediately.
      this.index = clamped;
      this.sync();
    }

    queueSync() {
      if (this.queued) return;
      this.queued = true;
      window.requestAnimationFrame(() => {
        this.queued = false;
        const width = this.stage.clientWidth || 1;
        const at = Math.round(this.stage.scrollLeft / width);
        if (at !== this.index) {
          this.index = Math.max(0, Math.min(at, this.slides.length - 1));
          this.sync();
        }
      });
    }

    sync() {
      this.thumbs.forEach((thumb, i) => {
        thumb.classList.toggle('is-active', i === this.index);
        thumb.setAttribute('aria-current', i === this.index ? 'true' : 'false');
      });

      // Three thumbnails are visible at a time, so the active one is kept
      // inside that window rather than scrolled to the edge of it.
      const active = this.thumbs[this.index];
      if (active && this.track) {
        const left = active.offsetLeft - this.track.offsetLeft;
        const right = left + active.offsetWidth;
        if (left < this.track.scrollLeft) {
          this.track.scrollLeft = left;
        } else if (right > this.track.scrollLeft + this.track.clientWidth) {
          this.track.scrollLeft = right - this.track.clientWidth;
        }
      }

      if (this.prev) this.prev.disabled = this.index === 0;
      if (this.next) this.next.disabled = this.index === this.slides.length - 1;
    }
  }

  if (!customElements.get('kallo-gallery')) {
    customElements.define('kallo-gallery', KalloGallery);
  }
})();
